import React from 'react'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Divider,
  Form,
  Grid,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import { CopyOutlined, PlusOutlined } from '@ant-design/icons'
import {
  listOffPlatformOrders,
  createOffPlatformOrder,
  cancelOffPlatformOrder,
} from '../../api/adminOffPlatformOrders'
import { listDeliveryLocations } from '../../api/deliveryLocations'
import { listShopLocationFees } from '../../api/shopLocationFees'
import { apiUrl } from '../../config/api'

const NEW_LOCATION_VALUE = '__new__'

async function fetchShopsList() {
  const r = await fetch(apiUrl('/api/shops'))
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

function statusTag(s) {
  if (s === 'cancelled') return <Tag color="red">ملغي</Tag>
  if (s === 'new') return <Tag color="blue">جديد</Tag>
  if (s === 'delivered') return <Tag color="green">تم التوصيل</Tag>
  return <Tag>{s}</Tag>
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text)
    message.success('تم نسخ الرابط')
  } catch {
    message.error('تعذر النسخ')
  }
}

const { useBreakpoint } = Grid

export default function AdminOffPlatformOrders() {
  const screens = useBreakpoint()
  const isMobile = !screens.md
  const [data, setData] = React.useState([])
  const [shops, setShops] = React.useState([])
  const [locations, setLocations] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [createOpen, setCreateOpen] = React.useState(false)
  const [createdLink, setCreatedLink] = React.useState(null)
  const [form] = Form.useForm()

  // Live form state for the create modal
  const shopId = Form.useWatch('shopId', form)
  const locationChoice = Form.useWatch('locationChoice', form)
  const newLocationName = Form.useWatch('newLocationName', form)
  const [existingFee, setExistingFee] = React.useState(null) // number | null
  const [feeLookupLoading, setFeeLookupLoading] = React.useState(false)

  async function load() {
    setLoading(true)
    try {
      const [rows, shopList, locs] = await Promise.all([
        listOffPlatformOrders(),
        fetchShopsList().catch(() => []),
        listDeliveryLocations().catch(() => []),
      ])
      setData(Array.isArray(rows) ? rows : [])
      setShops(Array.isArray(shopList) ? shopList : [])
      setLocations(Array.isArray(locs) ? locs : [])
    } catch (e) {
      console.error(e)
      message.error('فشل في تحميل البيانات')
    } finally {
      setLoading(false)
    }
  }
  React.useEffect(() => { load() }, [])

  // Whenever shop+existing-location is resolved, look up the saved fee (if any).
  React.useEffect(() => {
    let cancelled = false
    async function lookup() {
      const usingNew = locationChoice === NEW_LOCATION_VALUE
      if (!shopId || !locationChoice || usingNew) {
        setExistingFee(null)
        // Default the saveFeeRule checkbox to OFF whenever the target changes.
        form.setFieldsValue({ saveFeeRule: false })
        // For brand-new locations we always need a fee — leave the field as is for typing.
        if (usingNew) form.setFieldsValue({ deliveryFee: form.getFieldValue('deliveryFee') })
        return
      }
      setFeeLookupLoading(true)
      try {
        const rows = await listShopLocationFees({ shopId, locationId: locationChoice })
        if (cancelled) return
        const fee = Array.isArray(rows) && rows.length > 0 ? rows[0].fee : null
        setExistingFee(fee)
        // Pre-fill the fee field with the saved value; admin can override.
        form.setFieldsValue({ deliveryFee: fee == null ? null : Number(fee), saveFeeRule: false })
      } catch (e) {
        console.error(e)
        if (!cancelled) setExistingFee(null)
      } finally {
        if (!cancelled) setFeeLookupLoading(false)
      }
    }
    lookup()
    return () => { cancelled = true }
  }, [shopId, locationChoice, form])

  async function onCreate() {
    try {
      const values = await form.validateFields()
      const usingNew = values.locationChoice === NEW_LOCATION_VALUE
      const typedName = (values.newLocationName || '').trim()
      if (usingNew && !typedName) {
        message.error('أدخل اسم المنطقة الجديدة')
        return
      }
      const payload = {
        shopId: values.shopId,
        notes: values.notes || null,
        deliveryFee: values.deliveryFee == null ? null : Number(values.deliveryFee),
        saveFeeRule: !!values.saveFeeRule,
      }
      if (usingNew) payload.newLocationName = typedName
      else payload.deliveryLocationId = values.locationChoice

      const r = await createOffPlatformOrder(payload)
      if (!r.ok) {
        const err = r.body?.error
        if (err === 'delivery_fee_required') {
          message.error('رسوم التوصيل مطلوبة لهذه المنطقة (لا يوجد سعر محفوظ)')
        } else if (err === 'delivery_fee_negative') {
          message.error('رسوم التوصيل لا يمكن أن تكون سالبة')
        } else if (err === 'delivery_location_required') {
          message.error('اختر منطقة موجودة أو أدخل اسم منطقة جديدة')
        } else if (r.status === 404) {
          message.error('المتجر أو المنطقة غير موجودة')
        } else {
          message.error('تعذر إنشاء الطلب')
        }
        return
      }
      message.success('تم إنشاء الطلب')
      setCreateOpen(false)
      form.resetFields()
      setExistingFee(null)
      setCreatedLink(r.body?.claimLink || null)
      await load()
    } catch (e) {
      console.error(e)
    }
  }

  async function onCancel(id) {
    Modal.confirm({
      title: 'إلغاء الطلب',
      content: 'هل أنت متأكد من إلغاء هذا الطلب؟',
      okText: 'نعم',
      cancelText: 'تراجع',
      onOk: async () => {
        const r = await cancelOffPlatformOrder(id)
        if (!r.ok) { message.error('تعذر الإلغاء'); return }
        message.success('تم الإلغاء')
        await load()
      },
    })
  }

  const columns = [
    { title: 'المعرف', dataIndex: 'id', key: 'id', width: 80, sorter: (a, b) => a.id - b.id },
    { title: 'الحالة', dataIndex: 'status', key: 'status', render: statusTag, width: 100 },
    {
      title: 'تاريخ الإنشاء',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: v => v ? new Date(v).toLocaleString('ar') : '-',
      defaultSortOrder: 'descend',
      sorter: (a, b) => String(a.createdAt).localeCompare(String(b.createdAt)),
    },
    { title: 'المتجر', dataIndex: 'shopName', key: 'shopName' },
    {
      title: 'منطقة التوصيل',
      key: 'deliveryLocationName',
      render: (_, r) => (
        <Space size={4}>
          <span>{r.deliveryLocationName}</span>
          {r.deliveryLocationCustomerVisible === false && <Tag color="orange">مخفية</Tag>}
        </Space>
      ),
    },
    { title: 'رسوم التوصيل', dataIndex: 'deliveryFee', key: 'deliveryFee', width: 120 },
    {
      title: 'الكابتن',
      key: 'captain',
      render: (_, r) => r.assignedCaptainName
        ? `${r.assignedCaptainName}${r.assignedCaptainPhone ? ' (' + r.assignedCaptainPhone + ')' : ''}`
        : '-',
    },
    { title: 'ملاحظات', dataIndex: 'notes', key: 'notes', ellipsis: true, render: v => v || '-' },
    {
      title: 'إجراءات',
      key: 'actions',
      render: (_, r) => (
        <Space size="small" wrap>
          <Button
            size={isMobile ? 'small' : 'middle'}
            icon={<CopyOutlined />}
            onClick={() => copyToClipboard(r.claimLink)}
          >
            {isMobile ? '' : 'نسخ الرابط'}
          </Button>
          {r.status !== 'cancelled' && (
            <Button
              danger
              size={isMobile ? 'small' : 'middle'}
              onClick={() => onCancel(r.id)}
            >
              إلغاء
            </Button>
          )}
        </Space>
      ),
    },
  ]

  const locationOptions = [
    ...(locations || []).map(l => ({
      value: l.id,
      label: `${l.nameAr || l.name || `#${l.id}`}${l.isCustomerVisible === false ? ' (مخفية)' : ''}`,
    })),
    { value: NEW_LOCATION_VALUE, label: '➕ إضافة منطقة جديدة (مخفية افتراضياً)' },
  ]

  const usingNewLocation = locationChoice === NEW_LOCATION_VALUE
  const hasResolvedTarget = !!shopId && (usingNewLocation ? !!(newLocationName || '').trim() : !!locationChoice)
  const noSavedFee = !usingNewLocation && hasResolvedTarget && !feeLookupLoading && (existingFee == null)
  const showFeeRuleHint = hasResolvedTarget && (usingNewLocation || noSavedFee)

  return (
    <div
      dir="rtl"
      style={{
        maxWidth: 1400,
        margin: isMobile ? '12px auto' : '24px auto',
        padding: isMobile ? '0 8px' : '0 12px',
      }}
    >
      <Card
        title="الطلبات الخارجية"
        styles={{ body: { padding: isMobile ? 8 : 24 } }}
        extra={
          <Space wrap size="small">
            <Button onClick={load} size={isMobile ? 'small' : 'middle'}>تحديث</Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateOpen(true)}
              size={isMobile ? 'small' : 'middle'}
            >
              {isMobile ? 'إضافة' : 'إضافة طلب خارجي'}
            </Button>
          </Space>
        }
      >
        <Table
          rowKey="id"
          dataSource={data}
          columns={columns}
          loading={loading}
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: isMobile ? 10 : 25, simple: isMobile }}
        />
      </Card>

      <Modal
        open={createOpen}
        title="إضافة طلب خارجي"
        okText="إنشاء"
        cancelText="إلغاء"
        onOk={onCreate}
        onCancel={() => { setCreateOpen(false); form.resetFields(); setExistingFee(null) }}
        width={isMobile ? '95vw' : 560}
        style={isMobile ? { top: 12, paddingBottom: 0 } : undefined}
        styles={isMobile ? { body: { maxHeight: '70vh', overflowY: 'auto' } } : undefined}
        centered={!isMobile}
      >
        <Form form={form} layout="vertical" initialValues={{ saveFeeRule: false }}>
          <Form.Item name="shopId" label="المتجر" rules={[{ required: true, message: 'المتجر مطلوب' }]}>
            <Select
              showSearch
              placeholder="اختر المتجر"
              optionFilterProp="label"
              options={(shops || []).map(s => ({ value: s.id, label: s.nameAr || s.name || `#${s.id}` }))}
            />
          </Form.Item>

          <Form.Item
            name="locationChoice"
            label="منطقة التوصيل"
            rules={[{ required: true, message: 'المنطقة مطلوبة' }]}
          >
            <Select
              showSearch
              placeholder="اختر المنطقة أو أضف واحدة جديدة"
              optionFilterProp="label"
              options={locationOptions}
            />
          </Form.Item>

          {usingNewLocation && (
            <Form.Item
              name="newLocationName"
              label="اسم المنطقة الجديدة"
              rules={[{ required: true, message: 'اسم المنطقة مطلوب' }]}
            >
              <Input placeholder="مثلاً: نابلس - رفيديا" />
            </Form.Item>
          )}

          <Divider style={{ margin: '12px 0' }} />

          {showFeeRuleHint && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={
                usingNewLocation
                  ? 'هذه المنطقة غير مدعومة في النظام بعد — أدخل سعر التوصيل لهذا الطلب.'
                  : 'لا يوجد سعر توصيل محفوظ لهذا المتجر في هذه المنطقة — أدخل سعراً لهذا الطلب.'
              }
            />
          )}

          <Form.Item
            name="deliveryFee"
            label="رسوم التوصيل"
            rules={[{ required: true, message: 'الرسوم مطلوبة' }]}
            extra={
              feeLookupLoading
                ? 'جاري جلب السعر المحفوظ…'
                : (existingFee != null && !usingNewLocation
                    ? `السعر المحفوظ: ${existingFee} — يمكنك التعديل لهذا الطلب.`
                    : null)
            }
          >
            <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="saveFeeRule" valuePropName="checked">
            <Checkbox>
              {showFeeRuleHint
                ? 'حفظ هذا السعر كقاعدة دائمة لهذا المتجر + المنطقة'
                : 'تحديث السعر المحفوظ لهذا المتجر + المنطقة'}
            </Checkbox>
          </Form.Item>

          <Form.Item name="notes" label="ملاحظات (اختياري)">
            <Input.TextArea rows={3} maxLength={500} placeholder="مثلاً: الطلب جاهز خلال 10 دقائق" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={!!createdLink}
        title="رابط الكابتن جاهز"
        width={isMobile ? '95vw' : 520}
        centered
        footer={[
          <Button key="copy" type="primary" icon={<CopyOutlined />} onClick={() => copyToClipboard(createdLink)}>
            نسخ
          </Button>,
          <Button key="close" onClick={() => setCreatedLink(null)}>إغلاق</Button>,
        ]}
        onCancel={() => setCreatedLink(null)}
      >
        <Typography.Paragraph>
          أرسل الرابط التالي للكابتن أو إلى مجموعة الواتساب الخاصة بالمنطقة:
        </Typography.Paragraph>
        <Input value={createdLink || ''} readOnly />
        <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          سيتم إرسال رسالة تيليجرام تلقائياً أيضاً.
        </Typography.Text>
      </Modal>
    </div>
  )
}
